import * as React from 'react';
import * as UI from 'core/ui';
import * as Images from 'pos/Images';

import { Permission, PriceListConfigurationDTO, Res } from 'core/api';
import { SessionProvider, Result } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import { Command } from 'pos/common';
import { RestaurantFacade } from 'pos/facades';
import { ActionButtonDefinition, PosScreenClassComponent, ScreenSize } from 'pos/ui';
import * as SalesStore from '../SalesStore';
import { Cols, Col, Flex } from 'core/ui';
import { Message, Modal } from 'core/ui-next/modal';

type Props = {
	initialConfiguration: PriceListConfigurationDTO
} & Modal.Actions<PriceListConfigurationDTO>;

type State = {
	configuration: PriceListConfigurationDTO
};

class ConfigurePriceListScreen extends PosScreenClassComponent<Props, State> {

	private form = React.createRef<UI.Form<PriceListConfigurationDTO>>();

	protected getScreenId(): string {
		return 'CONFIGURE_PRICE_LIST';
	}

	protected getTitle(): string {
		return Res.Web.CONFIGURE_PRICE_LIST();
	}

	protected getImage(): string | SvgIcon {
		return Images.ConfigurePriceList;
	}

	protected useKeypad(): boolean {
		return false;
	}

	protected getSize(): ScreenSize {
		return 'small';
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		return [
			this.cancelButton(),
			{ title: Res.Web.CONFIGURE_PRICE_LIST_RESTORE_DEFAULT_BTN(), image: Images.Reset, action: this.restoreDefault },
			null,
			null,
			null,
			this.okButton(this.onOK)
		];
	}

	restoreDefault = async () => {
		const newItems = this.state.configuration.Items.map(i => {
			return { ...i, CurrentPriceList: i.AvailablePriceLists.find(a => a.IsDefault) || i.AvailablePriceLists[0] };
		});

		this.setState({
			configuration: {
				Items: newItems
			}
		}, () => this.form.current?.remountAll());

	};

	onOK = async () => {
		const valid = await this.form.current?.updateModel();
		if (valid)
			this.close(this.state.configuration);
	};

	protected getContent(): React.ReactNode {
		const { Form, ComboBox } = UI.controlsFor<PriceListConfigurationDTO>();

		const priceListsConfiguration = this.state.configuration.Items.map((i, index) => {
			return <Cols key={index}>
				<Col width='*'>
					{i.SaleCenterName}
				</Col>
				<Col width='*'>
					<ComboBox linkTo={`Items[${index}].CurrentPriceList`}
						options={i.AvailablePriceLists} required />
				</Col>
			</Cols>;
		});

		return <Flex.Vertical style={{ padding: '10px', overflowY: 'auto' }}>
			<Flex.Item>
				<p>{Res.Web.CONFIGURE_PRICE_LIST_SELECT_PRICE_LIST_INFO_LBL()}</p>
				<hr />
			</Flex.Item>
			<Flex.Item>
				<Form ref={this.form} model={this.state.configuration}>
					{priceListsConfiguration}
				</Form>
			</Flex.Item>
		</Flex.Vertical>;
	}

	createInitialState(props: Props): Nullable<State> {
		return { configuration: props.initialConfiguration };
	}

	onComponentDidMount(): void { }

	onComponentWillUnmount(): void { }

}

declare global {
	interface CommandType {
		configurePriceList: () => Promise<Result>;
	}
}

Command.register('configurePriceList', Preconditions.ensure(Permission.PriceList_Configure), async (): Promise<Result> => {

	if (SessionProvider.getCurrent().EnablePriceListSchedulerModule) {
		await Message.show(Res.Msg.MSG_CANNOT_CHANGE_PRICELIST_WHEN_AUTOMATIC_SCHEDULE_IS_ENABLED());
		return Result.error();
	}

	const configuration = await RestaurantFacade.getPriceListConfiguration();
	const result = await Modal.show(ConfigurePriceListScreen, { initialConfiguration: configuration });

	if (result.ok) {
		await SalesStore.setPriceListConfiguration(result.value);
	}

	return result;
});